// /investorsfront/src/components/authenticated-menu/AuthenticatedMenu.js
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, UserRound } from 'lucide-react';
import {
  FREE_MEMBERSHIP,
  STARTUP_MEMBERSHIP,
  getMembershipTranslationKey,
  normalizeMembershipType,
} from '../../hooks/membership';
import helloIcon from './hello.svg';

const AuthenticatedMenu = ({
  user,
  handleLogoutAndUpdateUser,
  domain,
  currentLanguage,
  languagePrefixes,
  itemClassName = '',
}) => {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const dropdownRef = useRef(null);

  const closeDropdown = () => {
    setShowDropdown(false);
    setShowLogoutConfirm(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      closeDropdown();
    }
  };

  useEffect(() => {
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const greetingMessage = user?.firstName ? `Hi ${user.firstName}!` : 'Hello!';
  const membershipType = normalizeMembershipType(user?.membershipType);
  const currentPlanLabel = t(getMembershipTranslationKey(membershipType));
  const showPlanAction =
    membershipType === FREE_MEMBERSHIP || membershipType === STARTUP_MEMBERSHIP;
  const accountHref = `${languagePrefixes[currentLanguage] || ''}/account`;
  const pricingHref = `${languagePrefixes[currentLanguage] || ''}/pricing`;
  const toggleDropdown = () => {
    if (showDropdown) {
      closeDropdown();
      return;
    }

    setShowDropdown(true);
  };

  return (
    <>
      <li>
        <Link
          to={`${languagePrefixes[currentLanguage] || ''}/app`}
          className={`${itemClassName} cursor-pointer`}
        >
          {t('menu.dashboard')}
        </Link>
      </li>

      <summary className="ml-1 relative" ref={dropdownRef}>
        <button
          onClick={toggleDropdown}
          className={`${itemClassName} p-1 cursor-pointer`}
          aria-haspopup="menu"
          aria-expanded={showDropdown}
          aria-label="Account menu"
        >
          <img
            className="object-cover w-7 h-7 rounded-full ring-2 ring-sky-500"
            src={user.avatarUrl}
            alt="User Avatar"
          />
        </button>

        {showDropdown && (
          <ul className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1rem)] bg-white border rounded shadow-lg z-50">
            <div className="flex items-center px-4 py-2 font-medium text-stone-950">
              <span>{greetingMessage}</span>
              <img src={helloIcon} alt="Hello icon" className="ml-2 w-6 h-6" />
            </div>
            <div className="px-4 pb-3">
              <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-stone-500">
                  {t('menu.currentPlan')}
                </p>
                <p className="mt-1 text-sm font-medium text-stone-900">
                  {currentPlanLabel}
                </p>
                {showPlanAction && (
                  <Link
                    to={pricingHref}
                    onClick={closeDropdown}
                    className="mt-2 inline-flex cursor-pointer items-center rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 transition-colors hover:bg-sky-100"
                  >
                    {t('menu.changePlan')}
                  </Link>
                )}
              </div>
            </div>
            <li>
              <Link
                to={accountHref}
                onClick={closeDropdown}
                className="flex cursor-pointer items-center gap-2 px-4 py-2 text-text1 hover:bg-stone-100 rounded-none"
              >
                <UserRound className="h-4 w-4 text-stone-500" />
                {t('menu.myAccount') || 'My account'}
              </Link>
            </li>
            <li>
              {showLogoutConfirm ? (
                <div className="mx-3 mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-medium text-stone-900">
                    {t('menu.logoutConfirmTitle')}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-stone-600">
                    {t('menu.logoutConfirmMessage')}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        closeDropdown();
                        handleLogoutAndUpdateUser();
                      }}
                      className="inline-flex cursor-pointer flex-1 items-center justify-center rounded-md bg-stone-900 px-3 py-2 text-xs font-medium text-white hover:bg-stone-800"
                    >
                      {t('menu.confirm')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLogoutConfirm(false)}
                      className="inline-flex cursor-pointer flex-1 items-center justify-center rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50"
                    >
                      {t('menu.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowLogoutConfirm(true)}
                  className="flex w-full cursor-pointer items-center gap-2 px-4 py-2 text-left text-text1 hover:bg-stone-100 rounded-none"
                >
                  <LogOut className="h-4 w-4 text-stone-500" />
                  {t('menu.logOut') || 'Log out'}
                </button>
              )}
            </li>
          </ul>
        )}
      </summary>
    </>
  );
};

export default AuthenticatedMenu;
